import { useCallback } from 'react';
import {
  MOSTRA_SEU_APROVA_PRESENTATION_KEY,
  MostraSeuAprovaTriggerKey,
  getContentKeyForTrigger,
} from './data';
import { useMostraSeuAprovaStore } from './store';
import { useCampaignState } from './useCampaignState';
import { useRegisterView } from './useRegisterView';

/**
 * Dispara um modal da campanha #mostraseuaprova a partir de uma ação concluída.
 *
 * Uso nos pontos de conclusão de cada produto:
 *
 *   const { fire } = useMostraSeuAprovaTrigger();
 *   // no onSuccess da mutation que conclui a ação:
 *   fire('simulador');
 *
 * `fire` é sempre seguro: fora da janela, com o aluno inelegível, com os três
 * slots gastos ou com a rede fora, ele não faz nada e não lança. Instrumentar um
 * ponto novo não tem como quebrar a tela.
 */
function useMostraSeuAprovaTrigger() {
  const { canFireTrigger, canShowPresentation } = useCampaignState();
  const { registerView } = useRegisterView();
  const open = useMostraSeuAprovaStore((state) => state.open);

  const fire = useCallback(
    async (triggerKey: MostraSeuAprovaTriggerKey): Promise<boolean> => {
      // Pré-condição local: evita ir ao servidor no caso comum, que é o aluno
      // não estar elegível. A decisão final continua sendo do servidor.
      if (!canFireTrigger && !canShowPresentation) return false;

      const { registered, kind } = await registerView({
        kind: 'trigger',
        triggerKey,
      });

      if (!registered) return false;

      // Quem ainda não viu a apresentação recebe o vídeo neste gatilho; a
      // variação do produto fica para o próximo, sem ter gasto slot.
      //
      // A origem do gatilho nem sempre é o conteúdo exibido: a navegação livre
      // registra `nav-livre` e mostra o texto de `cursos-regulares`.
      open(
        kind === 'presentation'
          ? MOSTRA_SEU_APROVA_PRESENTATION_KEY
          : getContentKeyForTrigger(triggerKey)
      );

      return true;
    },
    [canFireTrigger, canShowPresentation, open, registerView]
  );

  return { fire, canFire: canFireTrigger || canShowPresentation };
}

export { useMostraSeuAprovaTrigger };
